/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { createSelector } from 'reselect';

import { selectAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import { selectAllSkills } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import {
  checkForArtifactInCondition,
  getCallout
} from '../../util/dialog/DialogTreeUtil';

export const getNodeMap = state => state.dialog.nodeMap;
export const getChildMap = state => state.dialog.childMap;
export const getCurrentArtifact = (state, artifactName, artifactType) => {
  switch (artifactType) {
    case 'intent':
      return `#${artifactName}`;
    case 'entity':
      return `@${artifactName}`;
    default:
      break;
  }
  return null;
};

export const getNode = (state, nodeId) => {
  return state.nodeMap[nodeId];
};

export const setResponseNodesAttribute = (
  state,
  parentNodeId,
  attribute,
  value
) => {
  if (state.responseMap[parentNodeId]) {
    const responseIds = state.responseMap[parentNodeId];
    responseIds.forEach(nodeId => {
      const updatedNode = state.nodeMap[nodeId];
      _.set(updatedNode, attribute, value);
    });
  }
  return state;
};

export const getCalloutResultVars = state => {
  const vars = [];
  for (const id in state.nodeMap) {
    const node = state.nodeMap[id];
    const calloutAction = getCallout(node.actions);
    if (calloutAction) {
      vars.push(calloutAction.result_variable);
    }
  }

  return vars;
};

export const getNextCalloutId = state => {
  const calloutResultVars = getCalloutResultVars(state);
  const defaultCalloutResultVars = calloutResultVars.filter(
    defaultCalloutResultVar =>
      defaultCalloutResultVar.startsWith('webhook_result_')
  );
  let maxId = 0;
  defaultCalloutResultVars.forEach(function (defaultCalloutResultVar) {
    const idSuffix = defaultCalloutResultVar.split('_').pop();
    const resultVarId = parseInt(idSuffix);
    maxId = isNaN(resultVarId) || maxId > resultVarId ? maxId : resultVarId;
  });
  return maxId + 1;
};

export const isNodeHighlighted = (state, nodeId) => {
  if (state.highlightedNodes) {
    return !!state.highlightedNodes[nodeId];
  }
  return false;
};

export const getFirstResponseByConditions = (responses, conditions) => {
  if (responses) {
    const result = responses.filter(
      response => response.conditions === conditions
    );
    if (result.length > 0) {
      return result[0];
    }
  }

  return null;
};

export const getRootNodes = createSelector(
  [getNodeMap, getChildMap],
  (nodeMap, children) => {
    if (children) {
      const rootIds = children[undefined] || [];
      return rootIds.map(id => {
        return nodeMap[id];
      });
    }
    return [];
  }
);

export const isRootNodeLoading = state => {
  if (state.dialog) {
    const rootNodes = getRootNodes(state);
    return rootNodes.some(node => node.loading === true);
  }
  return false;
};

export const getReferencesToCurrentArtifact = createSelector(
  [getNodeMap, getCurrentArtifact],
  (nodeMap, artifact) => {
    if (nodeMap && artifact) {
      const references = [];
      for (const nodeName in nodeMap) {
        const node = nodeMap[nodeName];
        if (
          node.conditions &&
          checkForArtifactInCondition(node.conditions, artifact)
        ) {
          let reference = node;
          while (
            reference &&
            reference.type &&
            reference.type !== 'standard' &&
            reference.type !== 'frame'
          ) {
            if (!reference.parent) {
              break;
            }
            reference = nodeMap[reference.parent];
          }
          if (reference) {
            let { title } = reference;
            if (!title) {
              title = reference.condition || 'No condition set';
            }
            references.push(title);
          }
        }
      }
      return references;
    }
    return [];
  }
);

export const selectDialogSkillInAssistant = createSelector(
  [selectAssistant, selectAllSkills],
  (assistant, skills) => {
    if (!assistant || !Array.isArray(skills) || skills.length === 0) {
      return null;
    }
    const { skill_references } = assistant;
    if (!Array.isArray(skill_references) || skill_references.length === 0) {
      return null;
    }
    const dialogSkill = skills.find(
      skill =>
        skill_references.some(ref => ref.skill_id === skill.skill_id) &&
        skill.type === 'dialog'
    );
    return dialogSkill;
  }
);

//returns dialog skill that is not of action type
export const selectNonActionDialogSkill = createSelector(
  [selectAssistant, selectAllSkills],
  (assistant, skills) => {
    if (!assistant || !Array.isArray(skills) || skills.length === 0) {
      return null;
    }
    const { skill_references } = assistant;
    if (!Array.isArray(skill_references) || skill_references.length === 0) {
      return null;
    }

    const dialogSkill = skills.find(
      skill =>
        !skill.dialog_settings?.actions &&
        skill_references.some(ref => ref.skill_id === skill.skill_id) &&
        skill.type === 'dialog'
    );
    return dialogSkill;
  }
);
